import { cn } from '@/utils/styles';
import { Slot } from '@radix-ui/react-slot';
import { useDebounce } from '@/hooks/useDebounce';
import { useEffect, useRef, useState } from 'react';

const DEFAULT_SCROLL_OFFSET = 200;

export interface StickyNavBarProps<TContainer extends HTMLDivElement>
  extends React.ComponentProps<'nav'> {
  offset?: number;
  visible?: boolean;
  asChild?: boolean;
  container?: React.RefObject<TContainer | null>;
}

export const StickyNavBar = <TContainer extends HTMLDivElement>({
  asChild,
  children,
  className,
  container,
  offset = DEFAULT_SCROLL_OFFSET,
  ...rest
}: StickyNavBarProps<TContainer>) => {
  const [visible, setVisible] = useState(false);
  const wrapperRef = useRef<HTMLElement>(null);

  const Comp = asChild ? Slot : 'nav';

  const handleOnScroll = () => {
    const wrapper = wrapperRef.current;

    if (!wrapper) return;

    const scrollTop = container?.current?.scrollTop ?? window.scrollY;
    const scrollYDiff = scrollTop - wrapper.clientHeight;

    setVisible(scrollYDiff >= offset);
  };

  const debouncedHandleOnScroll = useDebounce({
    method: handleOnScroll,
    dependencyList: [container, offset],
  });

  useEffect(() => {
    const resolvedContainer = container?.current ?? window;

    resolvedContainer.addEventListener('scroll', debouncedHandleOnScroll);

    return () => {
      resolvedContainer.removeEventListener('scroll', debouncedHandleOnScroll);
    };
  }, [container, debouncedHandleOnScroll]);

  return (
    <Comp
      {...rest}
      ref={wrapperRef}
      data-state={visible}
      className={cn(
        'overflow-hidden opacity-0 data-[state=false]:pointer-events-none data-[state=true]:opacity-100 flex flex-row items-center min-h-12 top-0 inset-x-0 z-10 fixed border-b bg-clip-padding backdrop-filter bg-opacity-10 bg-background-blur-modal backdrop-blur-md gap-4 transition ease-in-out data-[state=false]:duration-300 data-[state=true]:duration-500 data-[state=true]:animate-in data-[state=false]:animate-out data-[state=false]:slide-out-to-top data-[state=true]:slide-in-from-top md:group-data-[open=true]/sidebar-wrapper:left-[--sidebar-width] max-md:group-data-[open-mobile=true]/sidebar-wrapper:left-[--sidebar-mobile-width]',
        className,
      )}
    >
      {children}
    </Comp>
  );
};
